import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  getOrderStatusByNonce,
  type OrderStatusByNonceResult,
} from '@/lib/api';
import { formatPrice } from '@/lib/format';
import { useTk } from '@/lib/i18n';

/* ------------------------------------------------------------------ */
/*  Internal helpers                                                   */
/* ------------------------------------------------------------------ */

type PageStatus = 'loading' | 'success' | 'error';

interface PageState {
  status: PageStatus;
  order: OrderStatusByNonceResult | null;
  errorMessage: string | null;
}

function orderStatusKey(status: string): string {
  return `order.status.${status.toLowerCase()}`;
}

/** Payment channel display key lookup */
function channelLabelKey(channel: string | null): string {
  if (!channel) return 'checkout.channel.mock.label';
  return `checkout.channel.${channel.toLowerCase()}.label`;
}

/** Map backend status to a visual variant */
type StatusVariant = 'pending' | 'processing' | 'success' | 'failure';

function statusVariant(status: string): StatusVariant {
  switch (status) {
    case 'CREATED':
    case 'PENDING':
      return 'pending';
    case 'SUCCEEDED':
    case 'VOUCHERED':
      return 'success';
    case 'FAILED':
    case 'EXPIRED':
    case 'CANCELLED':
    case 'REFUNDED':
      return 'failure';
    default:
      return 'pending';
  }
}

/** Whether the order is in a terminal (non-recoverable) state */
function isTerminal(status: string): boolean {
  return ['FAILED', 'EXPIRED', 'CANCELLED', 'REFUNDED'].includes(status);
}

/* ------------------------------------------------------------------ */
/*  Status icon component                                              */
/* ------------------------------------------------------------------ */

function StatusIcon({ variant }: { variant: StatusVariant }): React.ReactElement {
  const cls = 'w-16 h-16 mx-auto rounded-full flex items-center justify-center';

  switch (variant) {
    case 'pending':
      return (
        <div className={`${cls} bg-amber-50`}>
          <svg
            className="w-8 h-8 text-amber-500 animate-pulse"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
      );
    case 'processing':
      return (
        <div className={`${cls} bg-blue-50`}>
          <svg
            className="w-8 h-8 text-blue-500 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        </div>
      );
    case 'success':
      return (
        <div className={`${cls} bg-green-50`}>
          <svg
            className="w-8 h-8 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    case 'failure':
      return (
        <div className={`${cls} bg-red-50`}>
          <svg
            className="w-8 h-8 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
  }
}

/* ------------------------------------------------------------------ */
/*  Countdown component (for pending orders)                           */
/* ------------------------------------------------------------------ */

function Countdown({ expiresAt }: { expiresAt: string | null }): React.ReactElement {
  const tk = useTk();
  const [remaining, setRemaining] = useState<string | null>(null);

  useEffect(() => {
    if (!expiresAt) {
      setRemaining(null);
      return;
    }
    const targetMs = new Date(expiresAt).getTime();
    function tick(): void {
      const diff = targetMs - Date.now();
      if (diff <= 0) {
        setRemaining(tk('order.status.expired'));
        return;
      }
      const mins = Math.floor(diff / 60_000);
      const secs = Math.floor((diff % 60_000) / 1000);
      setRemaining(`${mins}:${String(secs).padStart(2, '0')}`);
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt, tk]);

  if (!remaining) return <span className="text-xs text-[var(--tg-hint)]">—</span>;

  return (
    <span className="font-mono text-sm font-semibold text-amber-600">{remaining}</span>
  );
}

/* ------------------------------------------------------------------ */
/*  Main page component                                                */
/* ------------------------------------------------------------------ */

export function OrderStatusPage(): React.ReactElement {
  const tk = useTk();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const nonce = searchParams.get('nonce');

  const [state, setState] = useState<PageState>({
    status: 'loading',
    order: null,
    errorMessage: null,
  });

  useEffect(() => {
    if (!id || !nonce) {
      setState({
        status: 'error',
        order: null,
        errorMessage: tk('order.detail.missing_order'),
      });
      return;
    }

    const ac = new AbortController();
    setState({ status: 'loading', order: null, errorMessage: null });

    getOrderStatusByNonce(id, nonce, ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return;
        setState({ status: 'success', order: res.order, errorMessage: null });
      })
      .catch((err: Error) => {
        if (ac.signal.aborted) return;
        setState({ status: 'error', order: null, errorMessage: err.message });
      });

    return () => ac.abort();
  }, [id, nonce, tk]);

  const order = state.order;
  const variant = order ? statusVariant(order.status) : 'pending';

  /* ---------- Render ---------- */

  return (
    <main className="min-h-screen px-6 py-8 space-y-6 pb-24">
      {/* Header */}
      <header className="flex items-center justify-between">
        <Link to="/" className="text-[var(--tg-link)] text-sm">
          ← {tk('nav.home')}
        </Link>
        <h1 className="text-xl font-semibold">{tk('order.detail.title')}</h1>
        <span />
      </header>

      {/* Loading state */}
      {state.status === 'loading' ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center text-neutral-500">
          {tk('common.loading')}
        </div>
      ) : null}

      {/* Error state */}
      {state.status === 'error' ? (
        <div className="rounded-lg border border-danger-500 bg-danger-50 p-4 text-sm text-danger-700">
          <p className="font-medium">{tk('common.error.generic')}</p>
          <p className="mt-1 break-all text-xs opacity-75">{state.errorMessage}</p>
        </div>
      ) : null}

      {/* Success state */}
      {state.status === 'success' && order ? (
        <>
          {/* Status hero */}
          <section className="rounded-2xl border border-neutral-200 bg-white p-6 text-center space-y-3">
            <StatusIcon variant={variant} />
            <p className="text-lg font-semibold">
              {tk(orderStatusKey(order.status), { fallback: order.status })}
            </p>
            <p className="text-sm text-[var(--tg-hint)]">{order.dealTitle}</p>
            <p className="text-2xl font-bold text-brand-600">
              {formatPrice(order.totalCents, order.currency)}
            </p>

            {/* Pending: countdown */}
            {variant === 'pending' ? (
              <div className="flex items-center justify-center gap-2 text-sm">
                <span className="text-[var(--tg-hint)]">{tk('order.detail.expires_at')}</span>
                <Countdown expiresAt={null} />
              </div>
            ) : null}

            {/* Processing: spinner hint */}
            {variant === 'processing' ? (
              <p className="text-xs text-[var(--tg-hint)]">
                {tk('order.detail.paid_at')}
              </p>
            ) : null}
          </section>

          {/* Voucher codes (success only) */}
          {variant === 'success' && order.voucherCodes.length > 0 ? (
            <section className="rounded-2xl border border-dashed border-green-200 bg-green-50/60 p-4 space-y-3">
              <p className="text-sm font-medium text-green-800">
                {tk('order.list.voucher_code')}
              </p>
              {order.voucherCodes.map((code, i) => (
                <div
                  key={code}
                  className="flex items-center justify-between rounded-xl bg-white px-4 py-3 shadow-sm"
                >
                  <span className="text-xs text-[var(--tg-hint)]">
                    {i + 1}
                  </span>
                  <span className="font-mono text-base font-bold tracking-widest text-green-700">
                    {code}
                  </span>
                </div>
              ))}
            </section>
          ) : null}

          {/* Order details */}
          <section className="rounded-2xl border border-neutral-200 bg-white p-4 space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-[var(--tg-hint)]">{tk('order.detail.total')}</span>
              <span className="font-semibold">
                {formatPrice(order.totalCents, order.currency)}
              </span>
            </div>
            {order.paymentChannel ? (
              <div className="flex items-center justify-between">
                <span className="text-[var(--tg-hint)]">{tk('order.detail.channel')}</span>
                <span>{tk(channelLabelKey(order.paymentChannel))}</span>
              </div>
            ) : null}
          </section>

          {/* Actions */}
          <div className="space-y-3">
            {isTerminal(order.status) ? (
              <Link
                to="/"
                className="block rounded-xl bg-brand-500 px-4 py-3 text-center text-sm font-medium text-white"
              >
                {tk('nav.home')}
              </Link>
            ) : null}

            <Link
              to="/me/orders"
              className="block rounded-xl border border-neutral-200 bg-white px-4 py-3 text-center text-sm font-medium text-[var(--tg-link)]"
            >
              {tk('order.detail.view_my_orders')}
            </Link>
          </div>
        </>
      ) : null}
    </main>
  );
}
